import { useState } from "react";
import type {
  GetOtpBalanceResponse,
  NotificationContact,
} from "@codway-dev/shared";
import type { CallbackEvent } from "@shopify/polaris-types";
import styles from "./OtpBalanceSection.module.css";

type Purchase = GetOtpBalanceResponse["recent_purchases"][number];
type Pack = GetOtpBalanceResponse["packages"][number];

const PURCHASE_STATUS_LABEL: Record<Purchase["status"], string> = {
  PENDING: "Procesando…",
  CONFIRMED: "Confirmado",
  DECLINED: "Rechazado",
  EXPIRED: "Expirado",
};

const PURCHASE_STATUS_TONE: Record<
  Purchase["status"],
  "info" | "success" | "critical" | "warning"
> = {
  PENDING: "info",
  CONFIRMED: "success",
  DECLINED: "critical",
  EXPIRED: "warning",
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("es", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function PackCard({
  pack,
  buying,
  onBuy,
}: {
  pack: Pack;
  buying: string | null;
  onBuy: (id: string) => void;
}) {
  const isLoading = buying === pack.id;
  const isPopular = pack.id === "growth";
  const isDisabled = buying !== null;

  return (
    <div style={{ position: "relative", paddingTop: "10px" }}>
      {isPopular && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#008060",
            color: "#fff",
            fontSize: "11px",
            fontWeight: 700,
            padding: "1px 10px",
            borderRadius: "99px",
            whiteSpace: "nowrap",
            zIndex: 1,
          }}
        >
          ★ POPULAR
        </div>
      )}
      <div
        onClick={() => !isDisabled && onBuy(pack.id)}
        className={styles.packCard}
        data-popular={isPopular || undefined}
        data-disabled={isDisabled || undefined}
        style={{
          position: "relative",
          borderRadius: "8px",
          padding: "16px",
          cursor: isDisabled ? "not-allowed" : "pointer",
          opacity: isDisabled && !isLoading ? 0.6 : 1,
          backgroundColor: "#fff",
          userSelect: "none",
        }}
      >
        <div
          style={{
            fontSize: "11px",
            fontWeight: 600,
            color: "#6d7175",
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            marginBottom: "8px",
          }}
        >
          {pack.name.toUpperCase()}
        </div>

        <div style={{ display: "flex", alignItems: "baseline", gap: "4px" }}>
          <span style={{ fontSize: "18px", fontWeight: 700, color: "#202223" }}>
            ${pack.price_usd}
          </span>
          <span style={{ fontSize: "12px", color: "#8c9196" }}>USD</span>
        </div>

        <div style={{ fontSize: "12px", color: "#6d7175", marginTop: "6px" }}>
          ≈{pack.est_verifications_whatsapp.toLocaleString("es")} WhatsApp ·{" "}
          {pack.est_verifications_sms.toLocaleString("es")} SMS
        </div>

        {isLoading && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: "rgba(255,255,255,0.8)",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ fontSize: "12px", color: "#6d7175" }}>
              Redirigiendo…
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function NotificationContactsForm({
  contacts,
  updating,
  onSave,
}: {
  contacts: NotificationContact[];
  updating: boolean;
  onSave: (contacts: NotificationContact[]) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const email = contacts.find((c) => c.type === "email")?.value ?? "";
  const whatsapp = contacts.find((c) => c.type === "whatsapp")?.value ?? "";
  const [emailInput, setEmailInput] = useState(email);
  const [whatsappInput, setWhatsappInput] = useState(whatsapp);

  const startEditing = () => {
    setEmailInput(email);
    setWhatsappInput(whatsapp);
    setEditing(true);
  };

  const handleSave = async () => {
    const next: NotificationContact[] = [];
    if (emailInput.trim())
      next.push({ type: "email", value: emailInput.trim() });
    if (whatsappInput.trim())
      next.push({ type: "whatsapp", value: whatsappInput.trim() });
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      // Stay in editing mode so the merchant can retry — the error itself
      // is surfaced via the balance error banner in OtpConfigPage.
    }
  };

  if (editing) {
    return (
      <s-stack direction="block" gap="small-200">
        <s-text-field
          label="Email"
          value={emailInput}
          onInput={(e: CallbackEvent<"s-text-field">) =>
            setEmailInput(e.currentTarget.value)
          }
        />
        <s-text-field
          label="WhatsApp"
          value={whatsappInput}
          onInput={(e: CallbackEvent<"s-text-field">) =>
            setWhatsappInput(e.currentTarget.value)
          }
        />
        <s-stack direction="inline" gap="small-200">
          <s-button variant="primary" disabled={updating} onClick={handleSave}>
            Guardar
          </s-button>
          <s-button onClick={() => setEditing(false)}>Cancelar</s-button>
        </s-stack>
      </s-stack>
    );
  }

  return (
    <s-stack direction="block" gap="small-200">
      <s-text color="subdued">
        Recibirás una notificación cuando tu saldo esté bajo en
      </s-text>
      <s-stack direction="inline" gap="small-200">
        {email && <s-badge>{email}</s-badge>}
        {whatsapp && <s-badge>{whatsapp}</s-badge>}
      </s-stack>
      <s-stack direction="inline" gap="small-200">
        <s-button onClick={startEditing} icon="edit">
          Cambia tus contactos
        </s-button>
        {!whatsapp && (
          <s-button onClick={startEditing} icon="chat">
            Añade tu WhatsApp
          </s-button>
        )}
      </s-stack>
    </s-stack>
  );
}

function PurchaseHistory({
  purchases,
  packNames,
}: {
  purchases: Purchase[];
  packNames: Record<string, string>;
}) {
  const [open, setOpen] = useState(false);
  if (purchases.length === 0) return null;

  return (
    <s-section>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          padding: "4px 0",
          background: "none",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <strong style={{ fontSize: "14px" }}>Historial de compras</strong>
        <span style={{ fontSize: "12px", color: "#6d7175" }}>
          {open ? "▲" : "▼"}
        </span>
      </button>

      {open && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {purchases.map((p, i) => (
            <div
              key={p.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 0",
                borderTop: i > 0 ? "1px solid #f1f2f3" : "none",
                gap: "12px",
              }}
            >
              <div>
                <div style={{ fontSize: "14px", fontWeight: 500 }}>
                  {packNames[p.package_id] ?? p.package_id}
                </div>
                <div style={{ fontSize: "12px", color: "#8c9196" }}>
                  {formatDate(p.created_at)}
                </div>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <span style={{ fontSize: "14px", fontWeight: 600 }}>
                  ${p.price_usd.toFixed(2)}
                </span>
                <s-badge tone={PURCHASE_STATUS_TONE[p.status]}>
                  {PURCHASE_STATUS_LABEL[p.status]}
                </s-badge>
              </div>
            </div>
          ))}
        </div>
      )}
    </s-section>
  );
}

export function OtpBalanceSection({
  balance,
  buying,
  updatingContacts,
  onBuy,
  onUpdateContacts,
}: {
  balance: GetOtpBalanceResponse;
  buying: string | null;
  updatingContacts: boolean;
  onBuy: (id: string) => void;
  onUpdateContacts: (contacts: NotificationContact[]) => Promise<void>;
}) {
  const totalBalance = balance.available_balance_usd + balance.free_balance_usd;
  const packNames = Object.fromEntries(
    balance.packages.map((pack) => [pack.id, pack.name])
  );

  return (
    <s-section heading="Saldo OTP">
      <s-stack direction="block" gap="large-200">
        <s-stack direction="block" gap="small-100">
          <s-text color="subdued">El saldo de tu cuenta</s-text>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: "8px",
              margin: "4px 0",
            }}
          >
            <span
              style={{
                fontSize: "40px",
                fontWeight: 700,
                color: "#202223",
                lineHeight: 1,
              }}
            >
              ${totalBalance.toFixed(2)}
            </span>
            <span style={{ fontSize: "18px", color: "#6d7175" }}>USD</span>
          </div>
        </s-stack>
        <div style={{ borderTop: "1px solid #f1f2f3", margin: "0" }} />

        <s-stack direction="block" gap="small-200">
          <s-text color="subdued">Recarga tu cuenta:</s-text>
          <s-grid
            gridTemplateColumns="repeat(auto-fit, minmax(140px, 1fr))"
            gap="small-300"
          >
            {balance.packages.map((pack) => (
              <PackCard
                key={pack.id}
                pack={pack}
                buying={buying}
                onBuy={onBuy}
              />
            ))}
          </s-grid>
        </s-stack>

        <NotificationContactsForm
          contacts={balance.notification_contacts}
          updating={updatingContacts}
          onSave={onUpdateContacts}
        />

        <s-text color="subdued">
          Has enviado {balance.verifications_sent.toLocaleString("es")}{" "}
          verificaciones.
        </s-text>
      </s-stack>

      <PurchaseHistory
        purchases={balance.recent_purchases}
        packNames={packNames}
      />
    </s-section>
  );
}
